import { useState, useEffect, useCallback } from "react";
import { User, Plus, X, Save, Trash2, Edit2, Camera, Instagram, Globe, Mail, Phone, RefreshCw } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const ROLES = ["DJ", "Host", "Producer", "Music Director", "Guest", "Personality", "News Anchor", "Sports Commentator"];

const emptyForm = {
  name: "", bio: "", role: "DJ", image_url: "",
  social_instagram: "", social_twitter: "", social_facebook: "",
  social_youtube: "", social_tiktok: "", social_website: "",
  contact_email: "", contact_phone: "",
};

export const DJProfilesTab = ({ currentUser, authToken }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [viewingProfile, setViewingProfile] = useState(null);

  const stationId = currentUser?.station_id || currentUser?.azuracast_station_id || currentUser?.whitelabel_slug;

  const flash = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4000);
  };

  const fetchProfiles = useCallback(async () => {
    if (!stationId) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/dj-profiles/${stationId}`);
      if (res.ok) {
        const data = await res.json();
        setProfiles(data.profiles || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [stationId]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const handleSave = async () => {
    if (!form.name.trim()) { flash("DJ name is required", "error"); return; }
    setSaving(true);
    try {
      const url = editingId
        ? `${BACKEND_URL}/api/dj-profiles/${editingId}`
        : `${BACKEND_URL}/api/dj-profiles`;
      const method = editingId ? "PUT" : "POST";
      const body = editingId ? form : { ...form, station_id: stationId };
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        flash(editingId ? "Profile updated" : "DJ profile created");
        setShowForm(false);
        setEditingId(null);
        setForm({ ...emptyForm });
        fetchProfiles();
      } else {
        const d = await res.json();
        flash(d.detail || "Failed to save", "error");
      }
    } catch (e) { flash(e.message, "error"); }
    finally { setSaving(false); }
  };

  const handleImageUpload = async (e, djId) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${BACKEND_URL}/api/dj-profiles/${djId}/image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        flash("Image uploaded");
        // Update local state
        setProfiles(prev => prev.map(p => p.dj_id === djId ? { ...p, image_url: data.image_url } : p));
        if (editingId === djId) setForm(prev => ({ ...prev, image_url: data.image_url }));
        if (viewingProfile?.dj_id === djId) setViewingProfile(prev => ({ ...prev, image_url: data.image_url }));
      } else flash("Upload failed", "error");
    } catch (e) { flash(e.message, "error"); }
    finally { setUploading(false); }
  };

  const handleDelete = async (djId) => {
    if (!window.confirm("Delete this DJ profile? Their shows will be unlinked.")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/dj-profiles/${djId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) { flash("DJ profile deleted"); fetchProfiles(); setViewingProfile(null); }
      else flash("Failed to delete", "error");
    } catch (e) { flash(e.message, "error"); }
  };

  const startEdit = (p) => {
    setForm({
      name: p.name, bio: p.bio || "", role: p.role || "DJ", image_url: p.image_url || "",
      social_instagram: p.social_instagram || "", social_twitter: p.social_twitter || "",
      social_facebook: p.social_facebook || "", social_youtube: p.social_youtube || "",
      social_tiktok: p.social_tiktok || "", social_website: p.social_website || "",
      contact_email: p.contact_email || "", contact_phone: p.contact_phone || "",
    });
    setEditingId(p.dj_id);
    setShowForm(true);
    setViewingProfile(null);
  };

  if (!stationId) {
    return (
      <div className="text-center py-16" data-testid="dj-profiles-no-station">
        <User className="w-12 h-12 text-white/15 mx-auto mb-3" />
        <p className="text-white/40 text-sm">Set up your station first to manage DJ profiles.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="dj-profiles-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <User className="w-5 h-5 text-purple-400" /> DJ Profiles
          </h3>
          <p className="text-white/40 text-sm mt-1">
            {profiles.length} DJ{profiles.length !== 1 ? "s" : ""} — assign to shows for On Air display
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchProfiles} className="p-2.5 text-white/40 hover:text-white rounded-xl hover:bg-white/5 transition" data-testid="dj-profiles-refresh">
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => { setShowForm(true); setEditingId(null); setForm({ ...emptyForm }); setViewingProfile(null); }}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition"
            data-testid="dj-profiles-add-btn"
          >
            <Plus className="w-4 h-4" /> Add DJ
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`px-4 py-2.5 rounded-xl text-sm font-medium ${
          message.type === "error" ? "bg-red-500/15 text-red-300 border border-red-500/20" : "bg-green-500/15 text-green-300 border border-green-500/20"
        }`}>{message.text}</div>
      )}

      {/* DJ Profile Detail Modal */}
      {viewingProfile && (
        <div className="bg-white/[0.03] border border-purple-500/20 rounded-2xl overflow-hidden" data-testid="dj-profile-detail">
          <div className="flex items-start gap-6 p-6">
            <div className="relative flex-shrink-0">
              {viewingProfile.image_url ? (
                <img src={viewingProfile.image_url} alt={viewingProfile.name} className="w-28 h-28 rounded-2xl object-cover border-2 border-purple-500/30" />
              ) : (
                <div className="w-28 h-28 rounded-2xl bg-purple-500/10 border-2 border-purple-500/20 flex items-center justify-center">
                  <User className="w-12 h-12 text-purple-400/40" />
                </div>
              )}
              <label className="absolute -bottom-2 -right-2 w-8 h-8 bg-purple-500 hover:bg-purple-600 rounded-full flex items-center justify-center cursor-pointer transition shadow-lg">
                <Camera className="w-4 h-4 text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e, viewingProfile.dj_id)} disabled={uploading} />
              </label>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-white text-xl font-bold">{viewingProfile.name}</h4>
                  <span className="text-purple-400 text-sm font-medium">{viewingProfile.role || "DJ"}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => startEdit(viewingProfile)} className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/10 transition" data-testid="dj-profile-edit-btn">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(viewingProfile.dj_id)} className="p-2 text-white/40 hover:text-red-400 rounded-lg hover:bg-red-500/10 transition" data-testid="dj-profile-delete-btn">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => setViewingProfile(null)} className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/10 transition">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {viewingProfile.bio && <p className="text-white/50 text-sm mt-2 leading-relaxed">{viewingProfile.bio}</p>}
              <div className="flex flex-wrap gap-2 mt-3">
                {viewingProfile.social_instagram && <a href={viewingProfile.social_instagram} target="_blank" rel="noopener noreferrer" className="text-xs px-2.5 py-1 rounded-lg bg-white/5 text-white/50 hover:text-white transition">Instagram</a>}
                {viewingProfile.social_twitter && <a href={viewingProfile.social_twitter} target="_blank" rel="noopener noreferrer" className="text-xs px-2.5 py-1 rounded-lg bg-white/5 text-white/50 hover:text-white transition">Twitter</a>}
                {viewingProfile.social_facebook && <a href={viewingProfile.social_facebook} target="_blank" rel="noopener noreferrer" className="text-xs px-2.5 py-1 rounded-lg bg-white/5 text-white/50 hover:text-white transition">Facebook</a>}
                {viewingProfile.social_youtube && <a href={viewingProfile.social_youtube} target="_blank" rel="noopener noreferrer" className="text-xs px-2.5 py-1 rounded-lg bg-white/5 text-white/50 hover:text-white transition">YouTube</a>}
                {viewingProfile.social_tiktok && <a href={viewingProfile.social_tiktok} target="_blank" rel="noopener noreferrer" className="text-xs px-2.5 py-1 rounded-lg bg-white/5 text-white/50 hover:text-white transition">TikTok</a>}
                {viewingProfile.social_website && <a href={viewingProfile.social_website} target="_blank" rel="noopener noreferrer" className="text-xs px-2.5 py-1 rounded-lg bg-white/5 text-white/50 hover:text-white transition">Website</a>}
              </div>
              {(viewingProfile.contact_email || viewingProfile.contact_phone) && (
                <div className="flex gap-4 mt-3 text-white/30 text-xs">
                  {viewingProfile.contact_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{viewingProfile.contact_email}</span>}
                  {viewingProfile.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{viewingProfile.contact_phone}</span>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white/[0.03] border border-purple-500/20 rounded-2xl p-6 space-y-5" data-testid="dj-profiles-form">
          <div className="flex items-center justify-between">
            <h4 className="text-white font-semibold">{editingId ? "Edit DJ Profile" : "New DJ Profile"}</h4>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-white/30 hover:text-white transition">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Image upload for existing profiles */}
          {editingId && (
            <div className="flex items-center gap-4">
              <div className="relative">
                {form.image_url ? (
                  <img src={form.image_url} alt="" className="w-20 h-20 rounded-xl object-cover border border-white/10" />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <User className="w-8 h-8 text-white/15" />
                  </div>
                )}
                <label className="absolute -bottom-1 -right-1 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-purple-600 transition">
                  <Camera className="w-3 h-3 text-white" />
                  <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e, editingId)} disabled={uploading} />
                </label>
              </div>
              <p className="text-white/30 text-xs">{uploading ? "Uploading..." : "Click the camera to change photo"}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white/50 text-xs mb-1.5 uppercase tracking-wider">Name *</label>
              <input
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="DJ Mike"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40"
                data-testid="dj-form-name"
              />
            </div>
            <div>
              <label className="block text-white/50 text-xs mb-1.5 uppercase tracking-wider">Role / Title</label>
              <select
                value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/40"
                data-testid="dj-form-role"
              >
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-white/50 text-xs mb-1.5 uppercase tracking-wider">Bio</label>
            <textarea
              value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })}
              placeholder="A brief bio about this DJ..."
              rows={3}
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40 resize-none"
              data-testid="dj-form-bio"
            />
          </div>

          <div>
            <label className="block text-white/50 text-xs mb-1.5 uppercase tracking-wider">Social Links</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input value={form.social_instagram} onChange={e => setForm({ ...form, social_instagram: e.target.value })}
                placeholder="Instagram URL" className="bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40" data-testid="dj-form-instagram" />
              <input value={form.social_twitter} onChange={e => setForm({ ...form, social_twitter: e.target.value })}
                placeholder="Twitter URL" className="bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40" data-testid="dj-form-twitter" />
              <input value={form.social_facebook} onChange={e => setForm({ ...form, social_facebook: e.target.value })}
                placeholder="Facebook URL" className="bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40" data-testid="dj-form-facebook" />
              <input value={form.social_youtube} onChange={e => setForm({ ...form, social_youtube: e.target.value })}
                placeholder="YouTube URL" className="bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40" data-testid="dj-form-youtube" />
              <input value={form.social_tiktok} onChange={e => setForm({ ...form, social_tiktok: e.target.value })}
                placeholder="TikTok URL" className="bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40" data-testid="dj-form-tiktok" />
              <input value={form.social_website} onChange={e => setForm({ ...form, social_website: e.target.value })}
                placeholder="Website URL" className="bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40" data-testid="dj-form-website" />
            </div>
          </div>

          <div>
            <label className="block text-white/50 text-xs mb-1.5 uppercase tracking-wider">Contact Info (optional)</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                placeholder="Email" className="bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40" data-testid="dj-form-email" />
              <input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })}
                placeholder="Phone" className="bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40" data-testid="dj-form-phone" />
            </div>
          </div>

          <button
            onClick={handleSave} disabled={saving}
            className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition"
            data-testid="dj-form-save"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving..." : editingId ? "Update Profile" : "Create Profile"}
          </button>

          {!editingId && (
            <p className="text-white/20 text-xs">You can upload a profile image after creating the DJ profile.</p>
          )}
        </div>
      )}

      {/* Profile Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-6 h-6 text-white/20 animate-spin" />
        </div>
      ) : profiles.length === 0 && !showForm ? (
        <div className="text-center py-16 bg-white/[0.02] border border-white/5 rounded-2xl" data-testid="dj-profiles-empty">
          <User className="w-14 h-14 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm mb-1">No DJ profiles yet</p>
          <p className="text-white/20 text-xs">Create your first DJ to assign to shows</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dj-profiles-grid">
          {profiles.map(p => (
            <div
              key={p.dj_id}
              onClick={() => { setViewingProfile(p); setShowForm(false); }}
              className="bg-white/[0.03] border border-white/8 rounded-2xl p-4 hover:bg-white/[0.05] hover:border-purple-500/20 transition cursor-pointer group"
              data-testid={`dj-card-${p.dj_id}`}
            >
              <div className="flex items-center gap-3">
                <div className="relative flex-shrink-0">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-14 h-14 rounded-xl object-cover border border-white/10" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                      <User className="w-6 h-6 text-purple-400/50" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{p.name}</p>
                  <p className="text-purple-400/70 text-xs font-medium">{p.role || "DJ"}</p>
                  {p.bio && <p className="text-white/25 text-xs mt-0.5 truncate">{p.bio}</p>}
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={e => { e.stopPropagation(); startEdit(p); }} className="p-1.5 text-white/30 hover:text-white rounded-lg hover:bg-white/10 transition">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); handleDelete(p.dj_id); }} className="p-1.5 text-white/30 hover:text-red-400 rounded-lg hover:bg-red-500/10 transition">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
